import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { apiJson } from '../api'
import { canManageEmployees } from '../auth'

interface Employee {
  id: string
  employeeCode: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  employmentStatus: string
  joinDate: string
  orgUnitId?: string
}

interface OrgUnit {
  id: string
  name: string
}

const EMPLOYMENT_STATUSES = ['applicant', 'hired', 'active', 'promoted', 'transferred', 'exited']

const statusMeta: Record<string, string> = {
  applicant: 'Applicant',
  hired: 'Hired',
  active: 'Active',
  promoted: 'Promoted',
  transferred: 'Transferred',
  exited: 'Exited',
}

const emptyForm = {
  employeeCode: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  joinDate: new Date().toISOString().slice(0, 10),
  employmentStatus: 'active',
  orgUnitId: '',
}

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

/** Dedicated add/edit page for employees (replaces the old modal). */
export default function EmployeeForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const navigate = useNavigate()
  const allowManage = canManageEmployees()

  const [form, setForm] = useState(emptyForm)
  const [orgUnits, setOrgUnits] = useState<OrgUnit[]>([])
  const [loading, setLoading] = useState(isEdit)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    apiJson<OrgUnit[]>('/org-units').then(setOrgUnits).catch(() => {})
  }, [])

  const fetchEmployee = useCallback(async () => {
    if (!isEdit) return
    try {
      const emp = await apiJson<Employee>(`/employees/${id}`)
      setForm({
        employeeCode: emp.employeeCode,
        firstName: emp.firstName,
        lastName: emp.lastName,
        email: emp.email,
        phone: emp.phone ?? '',
        joinDate: (emp.joinDate ?? '').slice(0, 10),
        employmentStatus: emp.employmentStatus,
        orgUnitId: emp.orgUnitId ?? '',
      })
      setLoadError('')
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load employee')
    } finally {
      setLoading(false)
    }
  }, [id, isEdit])

  useEffect(() => {
    fetchEmployee()
  }, [fetchEmployee])

  const save = async () => {
    setSaving(true)
    setSaveError('')
    const payload: Record<string, string> = {
      employeeCode: form.employeeCode.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      joinDate: form.joinDate,
      employmentStatus: form.employmentStatus,
    }
    if (form.phone.trim()) payload.phone = form.phone.trim()
    if (form.orgUnitId) payload.orgUnitId = form.orgUnitId
    try {
      if (isEdit) {
        await apiJson(`/employees/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
        navigate(`/employees/${id}`)
      } else {
        const created = await apiJson<Employee>('/employees', { method: 'POST', body: JSON.stringify(payload) })
        navigate(`/employees/${created.id}`)
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border border-amber-100 bg-amber-50 px-5 py-4 text-sm text-amber-700">
        <AlertCircle size={16} /> Employee records are managed by admin and HR roles.
      </div>
    )
  }
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }
  if (loadError) {
    return (
      <div className="space-y-4">
        <Link to="/employees" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700">
          <ArrowLeft size={14} /> Back to employees
        </Link>
        <div className="flex items-center gap-2 rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm text-red-600">
          <AlertCircle size={16} /> {loadError}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Link
        to={isEdit ? `/employees/${id}` : '/employees'}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700"
      >
        <ArrowLeft size={14} /> {isEdit ? 'Back to profile' : 'Back to employees'}
      </Link>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <h2 className="text-base font-bold tracking-tight">{isEdit ? 'Edit employee' : 'Add employee'}</h2>
          <p className="mt-0.5 text-xs text-slate-400">
            {isEdit ? `PATCH /employees · ${form.employeeCode}` : 'Creates a record via POST /employees · a hired history event is recorded automatically'}
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Employee code *</label>
              <input required value={form.employeeCode} onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} placeholder="EMP-001" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Join date *</label>
              <input required type="date" value={form.joinDate} onChange={(e) => setForm({ ...form, joinDate: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>First name *</label>
              <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Last name *</label>
              <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Department</label>
              <select value={form.orgUnitId} onChange={(e) => setForm({ ...form, orgUnitId: e.target.value })} className={inputCls}>
                <option value="">— Unassigned —</option>
                {orgUnits.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={form.employmentStatus} onChange={(e) => setForm({ ...form, employmentStatus: e.target.value })} className={inputCls}>
                {EMPLOYMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{statusMeta[s]}</option>
                ))}
              </select>
              {isEdit && (
                <p className="mt-1 text-[11px] text-slate-400">Status and department changes are recorded in the employment history.</p>
              )}
            </div>
          </div>

          {saveError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {saveError}
            </div>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
            <button
              type="button"
              onClick={() => navigate(isEdit ? `/employees/${id}` : '/employees')}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
